import { MeasurementType } from '@src/core/features/settings/entities';
import { UnitType } from '@src/core/enums';
import { getUnitType } from '@src/core/features/settings/units';

const CONVERSION_FACTORS: Record<MeasurementType, number> = {
  [MeasurementType.DISTANCE]: 3.28084,
  [MeasurementType.SPEED]:    2.23694,
  [MeasurementType.NONE]:     1,
};

export const arcConvertUnit = (
  value: number,
  measurementType: MeasurementType,
  unitType: UnitType,
): number => {
  if (unitType === UnitType.SI) return value;
  return Math.round(value * CONVERSION_FACTORS[measurementType]);
};

/**
 * display -> SI for a dimensional value. The single conversion point used on every
 * submit path: the wire is always SI, so the user's display-unit input is converted
 * back here before it leaves the frontend.
 *
 * No rounding — round-trip precision must be preserved (round only on display). See
 * UNITS Standard §6.1. Non-finite values and non-dimensional fields pass through.
 */
export const toSi = (
  value: number | string | null | undefined,
  measurementType: MeasurementType,
  unitType: UnitType = getUnitType(),
): number => {
  const num = Number(value);
  if (!Number.isFinite(num)) return num;
  if (unitType === UnitType.SI || measurementType === MeasurementType.NONE) return num;
  return num / CONVERSION_FACTORS[measurementType];
};

/** Convert a stored display value when the user switches unit system. */
export const arcConvertBetweenUnitTypes = (
  value: number,
  measurementType: MeasurementType,
  fromUnit: UnitType,
  toUnit: UnitType,
): number => {
  if (fromUnit === toUnit || !Number.isFinite(value)) return value;
  const factor = CONVERSION_FACTORS[measurementType];
  if (fromUnit === UnitType.SI && toUnit === UnitType.IMPERIAL) {
    return Math.round(value * factor * 10) / 10;
  }
  if (fromUnit === UnitType.IMPERIAL && toUnit === UnitType.SI) {
    return Math.round((value / factor) * 10) / 10;
  }
  return value;
};
